import { Children } from "react";
import {
  TableRow,
  TableCell,
  Typography,
  TableBody,
  styled,
  IconButton,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { memo } from "utils/memo";
import T from "utils/constants/T";
import { get } from "lodash";
import { useSelector } from "react-redux";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderTop: "inherit",
  overflow: "hidden",
  padding: "8px 0px 8px 10px",
}));

interface UnregisteredEmployeesBodyProps {
  allTableRows: any[];
  page: number;
  rowsPerPage: number;
  totalTableRowsCount: number;
  totalPageCount: number;
  opan: boolean;
  anchorEl: any;
  handleClick: any;
  handleClose: any;
  isCheckBoxSelected: any;
  handleRowCheckBoxClick: any;
  inviteUnregisteredEmployee: any;
  sendInvitationReminder: any;
  deleteUserProfile: any;
  selectedEmployeeId?: string;
  systemCode: string;
  isLoading?: boolean;
}
const UnregisteredEmployeesBody = ({
  allTableRows = [],
  page,
  rowsPerPage,
  totalTableRowsCount,
  totalPageCount,
  opan,
  anchorEl,
  handleClick,
  handleClose,
  isCheckBoxSelected,
  handleRowCheckBoxClick,
  inviteUnregisteredEmployee,
  sendInvitationReminder,
  deleteUserProfile,
  selectedEmployeeId,
  systemCode,
  isLoading = false,
}: UnregisteredEmployeesBodyProps) => {
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch
  );
  const companyId = get(selectedCompany, "_id", "");
  const { isManagerAdmin, isPayrollOfficer } = useSelector((state: any) => state.auth);
  // manager can only invite, payroll officer has no actions
  const canInviteEmployees = !isPayrollOfficer;
  const canDeleteEmployees = !isManagerAdmin && !isPayrollOfficer;

  const checkSalaryWag = (salaryWag: any, systemCode: any) => {
    let employeeType;
    if (systemCode === "DEPUTY") {
      employeeType = "Timesheet";
    } else {
      if (salaryWag === 2) {
        employeeType = "Timesheet";
      } else if (salaryWag === 3) {
        employeeType = "Salary & TimeSheet";
      } else {
        employeeType = "Salary";
      }
    }
    return employeeType;
  };

  return (
    <TableBody>
      {isLoading && allTableRows.length === 0 ? (
        <TableRow>
          <StyledTableCell colSpan={10} sx={{ border: "none" }}>
            <Typography variant="body1" textAlign="center">
              {T.LOADING}
            </Typography>
          </StyledTableCell>
        </TableRow>
      ) : (
        <>
        {allTableRows && allTableRows.length === 0 ? (
          <TableRow>
            <StyledTableCell colSpan={10} sx={{ border: "none" }}>
              <Typography variant="body1" textAlign="center">
                {T.NO_RECORDS}
              </Typography>
            </StyledTableCell>
          </TableRow>
        ) : (
          Children.toArray(allTableRows.map((record, index) => {
          const isItemSelected = isCheckBoxSelected(record._id);
          const labelId = `enhanced-table-checkbox-${index}`;
          const businessUnitName = get(record, "business_units[0].name");
          return (
            <TableRow
              hover
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={record._id}
              selected={isItemSelected}
              sx={{ background: "white" }}
            >
              <StyledTableCell>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onClick={(event) => handleRowCheckBoxClick(record._id)}
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    }
                    label=" "
                    checked={get(record, "selected", "")}
                  />
                </FormGroup>
              </StyledTableCell>
              {!companyId && (
                <StyledTableCell>
                  {get(record, "companies[0].company_name", "")}
                </StyledTableCell>
              )}
              <StyledTableCell>{get(record, "fullname", "")}</StyledTableCell>
              <StyledTableCell>{get(record, "email", "")}</StyledTableCell>
              <StyledTableCell>
                {get(record, "system_employee_id", "")}
              </StyledTableCell>
              <StyledTableCell>
                {checkSalaryWag(
                  get(record, "salary_wag", ""),
                  get(record?.companies[0]?.systems, "code", "")
                )}
              </StyledTableCell>
              <StyledTableCell>
                {record.is_invited === 0 ? (
                  <Chip
                    color="error"
                    label="Uninvited"
                    size="small"
                    variant="light"
                  />
                ) : (
                  <Chip
                    color="success"
                    label="Invited"
                    size="small"
                    variant="light"
                  />
                )}
              </StyledTableCell>
              <StyledTableCell>
                {get(record, "invitation_date", "-")}
              </StyledTableCell>
              {systemCode == T.ASTUTE.toUpperCase() && (
                <StyledTableCell>{businessUnitName}</StyledTableCell>
              )}
              <StyledTableCell>
                <IconButton
                  aria-controls={"basic-menu"}
                  aria-haspopup="true"
                  disabled={!canInviteEmployees && !canDeleteEmployees}
                  onClick={(e: any) => handleClick(e, record._id)} // set employee id for use in actions dropdown
                >
                  <MoreVertIcon />
                </IconButton>
              </StyledTableCell>

              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                keepMounted
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                {canInviteEmployees && (
                  <MenuItem
                    onClick={(e) => {
                      handleClose(e);
                      inviteUnregisteredEmployee(e);
                    }}
                  >
                    Invite
                  </MenuItem>
                )}
                {canInviteEmployees && (
                  <MenuItem onClick={(e) => {
                    handleClose(e);
                    sendInvitationReminder(e);
                  }}>
                    Send Reminder
                  </MenuItem>
                )}
                {canDeleteEmployees && (
                  <MenuItem onClick={(e) => {
                    handleClose(e);
                    deleteUserProfile(e);
                  }}>Delete</MenuItem>
                )}
              </Menu>
            </TableRow>
          );
        })
          )
        )}
        </>
      )}
    </TableBody>
  );
};

export default memo(UnregisteredEmployeesBody);
